(ns ui.view.card.activities.sub
  (:require
   [ui.util :as uu]
   [re-frame.core :as rf]
   [common.entity :as entity]
   [clojure.string :as string]
   [common.util :as cu]
   [ui.state.session.sub :as session-sub]
   [ui.view.card.sub :as common-sub]
   [common.localization :as loc]
   [clojure.set :as set]
   [frontend.account :as account]
   [common.date-formats :as date-formats]
   [common.timezones :as timezones]
   [ui.view.card.activities.model :as model]))

(def activity-entity-types
  #{:entity.type/note
    :entity.type/note-call
    :entity.type/note-meeting
    :entity.type/note-message
    :entity.type/email})

(def all-activities-tab-ui-config
  {:value :activity-view/tab-activity
   :icon :fa-globe})

(def activity-type->ui-tab-config
  {:entity.type/note {:value  :activity-view/tab-notes
                      :icon :fa-edit}
   :entity.type/email {:value  :activity-view/tab-emails
                       :icon :fa-envelope}
   :entity.type/note-call {:value   :activity-view/tab-calls
                           :icon :fa-phone}
   :entity.type/note-meeting {:value   :activity-view/tab-meetings
                              :icon :fa-users}
   :entity.type/note-message {:value  :activity-view/tab-messages
                              :icon :fa-comments}
   :entity.type/value-creation {:value   :activity-view/tab-value-creations
                                :icon :fa-dollar-sign}})

(def activity-content-attributes
  {:entity.type/note            [:note/message]
   :entity.type/note-message    [:note-message/message]
   :entity.type/note-call       [:note-call/message]
   :entity.type/note-meeting    [:note-meeting/message]
   :entity.type/email           [:email/snippet :email/body]
   :entity.type/system-activity nil ;; no-content
   })

(def expanded-activity-content-attributes
  {:entity.type/note            [:note/message]
   :entity.type/note-message    [:note-message/message]
   :entity.type/note-call       [:note-call/message]
   :entity.type/note-meeting    [:note-meeting/message]
   :entity.type/email           [:email/body]
   :entity.type/system-activity nil ;; no-content
   })

(def ui-tab-config-value->entity-type
  (->> activity-type->ui-tab-config
       (mapv (fn [[entity-type ui-config]]
               [(:value ui-config) entity-type]))
       (into {})))

(defn actor-display-name
  [{:keys [entity-or-id entities-by-id account]}]
  (let [actor (get entities-by-id entity-or-id entity-or-id)]
    (cond (= (:db/id account)
             (or (:db/id actor) entity-or-id))
          (loc/tr :you)
          (number? actor)
          (loc/tr :system)
          :else
          (entity/display-string actor))))

(defn meta-text
  [{:keys [entity account entities-by-id]}]
  (let [entity-type (:entity/type entity)
        creator-name (actor-display-name {:entity-or-id (:common/created-by entity)
                                          :entities-by-id entities-by-id
                                          :account account})

        caller-name (actor-display-name {:entity-or-id  (:note-call/call-made-by entity)
                                         :entities-by-id entities-by-id
                                         :account account})
        email-sender (cu/str-trim-lcase (str (:email/from entity)))

        all-accounts-and-persons
        (->> entities-by-id
             (filterv (fn [[_ entity]]
                        (or (= (:entity/type entity) :entity.type/account)
                            (= (:entity/type entity) :entity.type/person))))
             (mapv second))

        email-actor-name
        (if (= email-sender (cu/str-trim-lcase (str (:account/email account))))
          (loc/tr :you)
          (or (->> all-accounts-and-persons
                   (keep (fn [a-or-p]
                           (when (= email-sender
                                    (cu/str-trim-lcase
                                     (str (or (:account/email a-or-p)
                                              (:person/universal-email-address a-or-p)))))
                             (entity/display-string a-or-p))))
                   (first))
              email-sender))]
    (case entity-type
      :entity.type/note (loc/tr :activity-label-note
                            [creator-name])
      :entity.type/value-creation (loc/tr :activity-label-value-creation-activity
                                      [creator-name])
      :entity.type/email (loc/tr :activity-label-email [email-actor-name])
      :entity.type/note-call (if (= (:note-call/type entity) :inbound)
                               (loc/tr :activity-label-note-call-inbound [creator-name])
                               (loc/tr :activity-label-note-call-outbound
                                   [(or caller-name creator-name)]))
      :entity.type/note-meeting (loc/tr :activity-label-note-meeting
                                    [creator-name])
      :entity.type/note-message (loc/tr :activity-label-note-message
                                    [creator-name
                                     (case (:note-message/message-type entity)
                                       :message.type/other "" ;; do not show type for other
                                       (loc/tr (:note-message/message-type entity)))])
      (str "Activity meta text missing : " entity-type))))

(rf/reg-sub
 ::activities-loaded?
 common-sub/card-state-extractor
 (fn [[card-state]]
   (get-in card-state model/activities-loaded-path)))

(rf/reg-sub
 ::emails-loaded?
 common-sub/card-state-extractor
 (fn [[card-state]]
   (get-in card-state model/emails-loaded-path)))

(rf/reg-sub
 ::loading?
 (fn [[_ ctx]]
   [(rf/subscribe [::activities-loaded? ctx])
    (rf/subscribe [::emails-loaded? ctx])])
 (fn [[activities-loaded? emails-loaded?]]
   (or (not activities-loaded?)
       (not emails-loaded?))))

;; HACK
(rf/reg-sub
 ::all-entities
 (fn [[_ ctx]]
   [(rf/subscribe [::common-sub/entities ctx])
    (rf/subscribe [::session-sub/entities])])
 (fn [[card-entities session-entities]]
   (merge session-entities card-entities)))

(rf/reg-sub
 ::entity
 (fn [[_ ctx]]
   [(rf/subscribe [::all-entities ctx])])
 (fn [[entities] [_ {:keys [entity-id] :as _ctx}]]
   (get entities entity-id)))

(rf/reg-sub
 ::activity-tag-filter-options
 :<- [::session-sub/entities]
 (fn [entities-by-id]
   (->> entities-by-id
        (filter (fn [[eid entity]]
                  (= (:named-value/attribute entity) :activity/tag-tags)))
        (map (fn [[eid entity]]
               (let [label (entity/display-string entity)]
                 {:value eid
                  :label label})))
        (sort-by :label)
        (vec))))

(rf/reg-sub
 ::selected-activity-tag-filters
 common-sub/card-state-extractor
 (fn [[card-state]]
   (get-in card-state model/activity-tag-filters-path)))

(rf/reg-sub
 ::collapse-activity-state
 common-sub/card-state-extractor
 (fn [[card-state]]
   (get-in card-state model/collapse-activity-state-path)))

(rf/reg-sub
 ::collapse-all-activities?
 common-sub/card-state-extractor
 (fn [[card-state]]
   (get-in card-state model/collapse-all-activities-path)))

(rf/reg-sub
 ::collapse-activity?
 (fn [[_ ctx _activity-id]]
   [(rf/subscribe [::collapse-activity-state ctx])
    (rf/subscribe [::collapse-all-activities? ctx])])
 (fn [[collapse-state collapse-all-activities?]
      [_ _ctx activity-id]]
   (if (contains? collapse-state activity-id)
     (get collapse-state activity-id)
     collapse-all-activities?)))

(rf/reg-sub
 ::entity-related-activities
 (fn [[_ ctx]]
   [(rf/subscribe [::common-sub/entities ctx])])
 (fn [[entities-by-id]
      [_ ctx]]
   (let [primary-entity-id (:entity-id ctx)]
     (->> entities-by-id
          vals
          (filter
           (fn [entity]
             (let [entity-type (:entity/type entity)
                   related-entities (-> entity
                                        :common/related-entities
                                        (set))]
               (and (contains? activity-entity-types entity-type)
                    (contains? related-entities primary-entity-id)))))
          (sort-by :common/created-on)
          (reverse)))))

(rf/reg-sub
 ::selected-activity-tab
 common-sub/card-state-extractor
 (fn [[card-state]]
   (get-in card-state model/selected-activity-tab-path :activity-view/tab-activity)))

(rf/reg-sub
 ::visible-activity-tabs
 (fn [[_ ctx]]
   [(rf/subscribe [::entity-related-activities ctx])])
 (fn [[activities]]
   (let [visible-entity-types (->> activities
                                   (group-by :entity/type)
                                   (keys)
                                   (vec))
         visible-activity-type-tabs
         (->> visible-entity-types
              (mapv (fn [et]
                      (get activity-type->ui-tab-config et))))

         visible-tabs (->> visible-activity-type-tabs
                           (mapv (fn [{:keys [value] :as ui-config}]
                                   (assoc ui-config :label (loc/tr value))))
                           (sort-by :label)
                           (vec))

         visible-tabs (-> all-activities-tab-ui-config
                          (assoc :label (loc/tr :all))
                          (cons visible-tabs)
                          (vec))]
     visible-tabs)))

(rf/reg-sub
 ::visible-activities
 (fn [[_ ctx]]
   [(rf/subscribe [::entity-related-activities ctx])
    (rf/subscribe [::selected-activity-tab ctx])
    (rf/subscribe [::selected-activity-tag-filters ctx])])
 (fn [[all-activities
       selected-activity-tab
       tag-filters]
      _arg-vec]
   (let [filters-set (set tag-filters)
         activities-filtered-by-tag
         (if (empty? filters-set)
           all-activities
           (->> all-activities
                (filterv
                 (fn [activity]
                   (let [tags (:activity/tag-tags activity)]
                     (seq (set/intersection tags filters-set)))))))

         visible-entity-type (get ui-tab-config-value->entity-type selected-activity-tab)

         visible-activities (cond
                              (= selected-activity-tab :activity-view/tab-activity)
                              activities-filtered-by-tag

                              :else
                              (->> activities-filtered-by-tag
                                   (filterv (fn [{:entity/keys [type]}]
                                              (= type visible-entity-type)))))]
     visible-activities)))

;; TODO: Split into smaller parts
(rf/reg-sub
 ::activity-data
 (fn [[_ ctx _activity-id]]
   [(rf/subscribe [::common-sub/entities ctx])
    (rf/subscribe [::session-sub/entities])
    (rf/subscribe [::session-sub/legacy-entities])
    (rf/subscribe [::session-sub/account])
    (rf/subscribe [::session-sub/tenant])])
 (fn [[card-entities-by-id
       global-entities-by-id
       global-legacy-entities-by-id
       account
       tenant]
      [_ ctx activity-id]]
   (let [primary-entity-id (:entity-id ctx)
         entities-by-id (merge
                         global-legacy-entities-by-id
                         global-entities-by-id
                         card-entities-by-id)

         df (or (:tenant/date-format tenant) (loc/tr :date-format))
         tz (or (:tenant/timezone tenant) timezones/default-timezone)

         entity (get card-entities-by-id activity-id)
         entity-type (:entity/type entity)
         created-on (:common/created-on entity)
         last-updated-by (:common/updated-by entity)
         last-updated-on (:common/updated-on entity)
         tag-ids (:activity/tag-tags entity)

         other-related-entities
         (->> entity
              :common/related-entities
              (remove (fn [eid]
                        (= eid primary-entity-id)))
              (map #(get entities-by-id %)))

         primary-entity (get entities-by-id primary-entity-id)
         primary-entity-attachments (set (:common/attachments primary-entity))

         attachments
         (->> entity
              :common/attachments
              (mapv (partial get entities-by-id))
              (mapv (fn [attachment]
                      (let [attachment-id (:db/id attachment)]
                        {:value attachment-id
                         :label (entity/display-string attachment)
                         :attached-to-primary-entity? (contains? primary-entity-attachments attachment-id)}))))

         also-associated-with
         (->> other-related-entities
              (mapv (fn [entity]
                      (let [entity-type (:entity/type entity)
                            label (entity/display-string entity)
                            icon (entity/default-icon entity)]
                        {:value (:db/id entity)
                         :entity-type entity-type
                         :label label
                         :icon (when icon (name icon))}))))

         tags (->> tag-ids
                   (mapv (fn [id]
                           (let [tag (get entities-by-id id)]
                             {:value id
                              :label (entity/display-string tag)}))))

         expanded? false

         show-last-updated-on? (and last-updated-on
                                    last-updated-by
                                    ;; HACK: to hide last updated by in data enrichment
                                    (> (- last-updated-on created-on) 2000))

         last-updated-on-text (when show-last-updated-on?
                                (loc/tr :activity-entry-updated-text
                                    [(account/actor-display-name last-updated-by :middle-of-sentence? true)
                                     (date-formats/format-millis last-updated-on df tz)]))

         date (uu/display-date-timestamp tenant created-on)
         meta (meta-text {:entity entity
                          :account account
                          :entities-by-id entities-by-id})

         content-attributes (if expanded?
                              (get expanded-activity-content-attributes entity-type)
                              (get activity-content-attributes entity-type))
         content (some->> content-attributes
                          (map (fn [content-attribute]
                                 (get entity content-attribute)))
                          (remove cu/blank-value?)
                          (first)
                          (str)
                          (string/trim))
         entity-name (entity/display-string entity)]
     {:title meta
      :last-updated-on-text last-updated-on-text
      :date date
      :icon (entity/default-icon entity)
      :tags tags
      :content content
      :attachments attachments
      :also-associated-with also-associated-with})))

